import { Link } from 'react-router'

import { useFaqCollection } from '@entities/Faq'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { resolveLandingAction } from '@features/landing-page-builder/model/resolveTarget'

import Accordion from '@shared/ui/accordion'

import styles from './LandingFaqPreviewBlock.module.scss'

/*
  faq_preview references a FaqCollection by code/id and carries no questions of
  its own — the content is loaded separately and rendered as an accordion.

  `expandMode` / `defaultExpandedIds` come from the collection: the backend
  decides which items open on arrival, the block only renders that decision.
*/
interface FaqPreviewPayload {
  title?: string;
  faqCollectionId?: string;
  faqCollectionCode?: string;
  /** Current contract; linkText/linkActionId are the older field names. */
  buttonLabel?: string;
  buttonActionId?: string;
  linkText?: string;
  linkActionId?: string;
}

export default function LandingFaqPreviewBlock ({ block, payload, actions }: LandingBlockProps) {
  const {
    title,
    faqCollectionId,
    faqCollectionCode,
    buttonLabel,
    buttonActionId,
    linkText,
    linkActionId,
  } = (payload ?? {}) as FaqPreviewPayload

  const { faqCollection, items, loading } = useFaqCollection({
    id: faqCollectionId,
    code: faqCollectionCode,
  })

  const linkLabel = buttonLabel || linkText
  const target = resolveLandingAction(actions, buttonActionId || linkActionId, block.target)

  const heading = title || faqCollection?.title

  if (!loading && items.length === 0) {
    return null
  }

  const defaultExpandedIds = (faqCollection?.defaultExpandedIds ?? []).map(String)

  return (
    <section className={styles.root}>
      {heading && <h2 className={styles.root__title}>{heading}</h2>}
      {faqCollection?.subtitle && (
        <p className={styles.root__subtitle}>{faqCollection.subtitle}</p>
      )}

      <div className={styles.root__list}>
        {items.map((item) => (
          <Accordion
            key={item.id}
            className={styles.item}
            triggerClassName={styles.item__trigger}
            contentClassName={styles.item__content}
            defaultOpen={defaultExpandedIds.includes(String(item.id))}
            trigger={<h3 className={styles.item__question}>{item.question}</h3>}
            content={<div className={styles.item__answer}>{item.answer}</div>}
          />
        ))}
      </div>

      {linkLabel && target && (
        target.external ? (
          <a
            className={styles.root__link}
            href={target.href}
            target="_blank"
            rel="noreferrer noopener"
          >
            {linkLabel}
          </a>
        ) : (
          <Link className={styles.root__link} to={target.href}>{linkLabel}</Link>
        )
      )}
    </section>
  )
}
